import { type FC } from 'react'

import { useAcceptCameraInvite } from '@liveHelper/hooks/useAcceptCameraInvite'
import { useConversationAction } from '@liveHelper/hooks/useConversationAction'
import { useFileUpload } from '@liveHelper/hooks/useFileUpload'
import { useLoadMoreMessages } from '@liveHelper/hooks/useLoadMoreMessages'
import {
  actionsSelector,
  cameraEnabledSelector,
  isAgentTypingSelector,
  isWaitingForVisitorInputSelector,
  messagesSelector,
  paramsSelector,
  useStore,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import { ChatContainer } from '@oneToOne/shared/components/chat/ChatContainer'
import {
  DEFAULT_TYPING_INDICATOR_THROTTLE_DELAY,
  TYPING_MESSAGE,
} from '@oneToOne/shared/components/chat/constants'
import { ChatHeader } from '@oneToOne/shared/components/twind/ChatHeader'
import { ChatInput } from '@oneToOne/shared/components/twind/ChatInput'
import throttle from 'lodash/throttle'

import { useContextTranslation } from '@core/i18n'

import { OnCallChatWrapper } from './OnCallChat.styles'

/**
 * On Call Chatting Component
 */
export const OnCallChat: FC = () => {
  const { t } = useContextTranslation()
  const messages = useStore(messagesSelector)
  const params = useStore(paramsSelector)
  const isAgentTyping = useStore(isAgentTypingSelector)
  const { sendTextMessage, sendTypingActivity } = useStore(
    visitorPrivateActionsSelector,
  )
  const { showFullScreenPreview, declineCameraInvite } =
    useStore(actionsSelector)
  const visitorCanUseCamera = useStore(cameraEnabledSelector)
  const isWaitingForVisitorInput = useStore(isWaitingForVisitorInputSelector)

  const loadMoreMessages = useLoadMoreMessages()
  const handleFileChange = useFileUpload()
  const { onEndConversationConversationAction, onJoinQueueConversationAction } =
    useConversationAction()
  const onAcceptCameraInvite = useAcceptCameraInvite()

  const throttleTypingActivityHandler = throttle(
    sendTypingActivity || (() => undefined),
    DEFAULT_TYPING_INDICATOR_THROTTLE_DELAY,
    {
      leading: true,
    },
  )

  return (
    <OnCallChatWrapper>
      <ChatContainer
        enableLink={true}
        lang={params.lang}
        messages={isAgentTyping ? [...messages, TYPING_MESSAGE] : messages}
        loadMoreMessages={loadMoreMessages}
        onSendMessage={(payload) =>
          sendTextMessage?.(payload.message, payload.isConversationAction)
        }
        onEndConversation={onEndConversationConversationAction}
        onJoinQueue={onJoinQueueConversationAction}
        renderChatHeader={() => <ChatHeader title={t('oneToOne:Chat')} />}
        chatInput={
          !isWaitingForVisitorInput
            ? () => (
                <ChatInput
                  onEnter={sendTextMessage}
                  typingActivityHandler={throttleTypingActivityHandler}
                  handleFileChange={handleFileChange}
                />
              )
            : undefined
        }
        showFullScreenPreview={showFullScreenPreview}
        pipParams={params}
        isCameraInviteActive={visitorCanUseCamera}
        onDeclineCameraInvite={declineCameraInvite}
        onAcceptCameraInvite={onAcceptCameraInvite}
      />
    </OnCallChatWrapper>
  )
}
